import { useHistory } from 'react-router-dom'

import { Choices } from '@customTypes/Choices'
import { Config } from '@customTypes/Config'

import { useConfig } from '@context/ConfigurationContext'
import { useChoiceState } from '@context/ChoiceContext'
import useComponentDidMount from '@hooks/useComponentDidMount'

import routes from 'src/config/routes'
import { isEmpty } from 'lodash'
import { PatientTypeOptions } from 'src/config/patientTypeOptions'

type WizardManagerProps = {
  targetRoute: routes
  skip?: boolean
  onValidStep?: () => void
}

/**
 * This represents the flow of the wizard,
 * each entry in the array is a step from the wizard
 * and the place in the user flow
 */
const wizard = [
  {
    selected: (choices: Choices) => Boolean(choices['treatmentBundleTypeReferenceId']),
    skippable: () => false,
    route: routes.treatment,
  },
  {
    selected: (choices: Choices) => Boolean(choices['treatmentBundleTypeReferenceId']),
    skippable: () => false,
    route: routes.clinic,
  },
  {
    selected: (choices: Choices) => Boolean(choices['treatmentBundleTypeReferenceId']),
    skippable: () => false,
    route: routes.district,
  },
  {
    selected: (choices: Choices) => Boolean(choices['patientType']),
    skippable: () => false,
    route: routes.patient,
  },
  {
    selected: (choices: Choices) => !isEmpty(choices['preferredPractitionerReferenceIds']),
    skippable: () => true,
    route: routes.practitioner,
  },
  {
    selected: (choices: Choices) => choices.patientType === PatientTypeOptions.OLD,
    skippable: (choices: Choices) => choices.patientType === PatientTypeOptions.NEW,
    route: routes.oldPatientPhoneVerification,
  },
  {
    selected: (choices: Choices) => choices.patientType === PatientTypeOptions.OLD,
    skippable: (choices: Choices) => choices.patientType === PatientTypeOptions.NEW,
    route: routes.oldPatientSelectAccount,
  },
  {
    selected: (choices: Choices) =>
      choices?.selectedBookableDateAndTime?.day && choices?.selectedBookableDateAndTime?.time,
    skippable: () => false,
    route: routes.appointment,
  },
  {
    selected: () => true,
    skippable: () => false,
    route: routes.contact,
  },
]

/**
 * Using the wizard array we find the index of the targeted route
 */
const getIndexOfTarget = (targetRoute: routes): number => {
  return wizard.map(({ route }) => route).indexOf(targetRoute)
}

/**
 * By looping thru the wizard and checking the selection of each step
 * we can figure out which is the current ( the step the user is supposed to be ) step
 * if a step is not skippable and does not have a selection we break out of the loop
 * in order to return currentIndex as the step where the user should be
 */
const getIndexOfCurrent = (choices: Choices, config: Config, targetIndex: number): number => {
  let currentIndex

  for (currentIndex = 0; currentIndex < targetIndex; currentIndex++) {
    const step = wizard[currentIndex]

    const isSkippable = step?.skippable(choices)
    const isSelected = step?.selected(choices)

    if (!isSkippable && !isSelected) {
      break
    }
  }

  return currentIndex
}

/**
 * This function makes sure that the user is on the correct step,
 * and protects from tampering ( e.g. setting the hash route manualy from the console )
 * It does so by validating the targetRoute against the current selection
 * @param targetRoute is provided from the screen that is being loaded
 * @param skip can be used to skip the wizard logic and load the requested screen
 * @param onValidStep a callback wich is fired if user is on the correct step
 */
const useWizardManager = ({ targetRoute, skip, onValidStep = () => null }: WizardManagerProps): void => {
  const choices = useChoiceState()
  const config = useConfig()
  const history = useHistory()

  useComponentDidMount(() => {
    const indexOfTarget = getIndexOfTarget(targetRoute)
    const indexOfCurrent = getIndexOfCurrent(choices, config, indexOfTarget)

    /**
     * if both indexOfTarget and indexOfCurrent have the same value
     * we assume that the user is on the correct step otherwise,
     * the user is being forwarded to the correct route/step
     */
    if (indexOfTarget === indexOfCurrent || skip) {
      /**
       * If onValidStep is passed from the screen
       * it will know what to execute.
       * For example, it will execute the correct analytics event
       */
      onValidStep()
    } else {
      history.push(wizard[indexOfCurrent].route)
    }
  })
}

export default useWizardManager
